/**
 * @file index.tsx
 * @author liuchen(loocliu@tencent.com)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Loading, MessagePlugin, Space, Tooltip } from 'tdesign-react';

import { CardFormComponent } from '@/components';
import NovaPrompt from '@/components/novaPrompt';
import TipDialog, { ITipDialogRef } from '@/components/tipDialog';
import { IFormResult } from '@/interface';
import { INovelHome } from '@/interface/novel';
import { NOVEL_HOME_LIST } from '@/router/const';
import { getNovelHomeInfo, modifyNovelHome } from '@/services/novel';
import { getFormErrorMsg } from '@/utils/getFormErrors';

import { baseFormCard } from './form-config';

const NovelHomeInfo = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('id');

  const cardFormRef = useRef<any>(null);
  const promptRef = useRef<boolean>(false);
  const tipRef = useRef<ITipDialogRef>(null);

  const [errorMsg, setErrorMsg] = useState('');
  const [isDraft, setIsDraft] = useState(true);
  const [novelHome, setNovelHome] = useState<Partial<INovelHome>>({});

  const save = () => {
    setIsDraft(false);
    cardFormRef.current.formRef.form.submit();
  };

  const handleSubmit = useCallback(async () => {
    promptRef.current = false;
    try {
      const res = await modifyNovelHome(novelHome);
      MessagePlugin.success('保存成功');
      if (!id) {
        setSearchParams({ id: res });
      }
    } catch (error: any) {
      MessagePlugin.error(error?.message);
    }
  }, [novelHome]);

  const onValuesChange = useCallback((content: { [key: string]: any }) => {
    promptRef.current = true;
    if ('twoColumnLayout' in content) {
      setNovelHome({ ...novelHome, twoColumnLayout: { novels: content.twoColumnLayout } });
    } else {
      setNovelHome({ ...novelHome, ...content });
    }
  }, [novelHome]);

  const handleReturn = () => {
    navigate(NOVEL_HOME_LIST, { replace: false });
  };


  const handleGetErrorMsg = (result: IFormResult['result']) => {
    const errors = getFormErrorMsg(result);
    setErrorMsg(errors);
    if (errors) {
      tipRef.current?.open();
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    getNovelHomeInfo(id).then((res) => {
      setNovelHome(res);
    })
      .catch((err) => {
        MessagePlugin.error(err.message);
      });
  }, [id]);

  const keys = Boolean((!novelHome?.id && id));

  return (
    <div key={`${keys}`} style={{ padding: 16 }}>
      <Loading size="large" key={`${keys}`} loading={keys} showOverlay>
        <NovaPrompt when={promptRef.current} />
        <TipDialog tipRef={tipRef} message={errorMsg} />
        <Space direction="vertical" size="large" style={{ width: '100%', minWidth: 1000 }}>
          <CardFormComponent
            key={'base'}
            cRef={cardFormRef}
            title={''}
            pageId='promotion'
            onSubmit={handleSubmit}
            formData={{ ...novelHome, twoColumnLayout: novelHome?.twoColumnLayout?.novels || [] }}
            fieldsConfig={baseFormCard.fieldsConfig}
            formConfig={{
              layout: 'vertical',
              labelWidth: 120,
              labelAlign: 'top',
              onValuesChange,
              showErrorMessage: !isDraft,
            }}
            extendControlConfig={{
              refactorFieldsConfig: config => config,
              onSubmitFail: (result: IFormResult['result']) => {
                handleGetErrorMsg(result);
              },
            }}
          />
        </Space>
        <div className="nova-fixed-bottom-wrapper-end">
          <Space>
            <Tooltip
              content={'返回首页配置列表'}
              destroyOnClose
              showArrow
              theme="default"
            >
              <Button
                className="nova-info-button"
                size="medium"
                block
                theme="default"
                onClick={handleReturn}
              >
                返回
              </Button>
            </Tooltip>
            <Button
              className="nova-info-button"
              size="medium"
              block
              theme="primary"
              onClick={save}
            >
              保存
            </Button>
          </Space>
        </div>
      </Loading>
    </div>
  );
};

export default NovelHomeInfo;
